// Файл TaggedInput.js

/**
 * TaggedInput расширяет обычное поле ввода, не заменяя его механизм
 * редактирования: нативный input или textarea по-прежнему хранит цельную
 * строку, принимает клавиатурный ввод, выделение, вставку и управляет
 * положением курсора. Его текст делается визуально прозрачным, а под полем
 * синхронно располагается зеркальный слой, в котором та же строка разбивается
 * на обычный текст, корректные теги и ошибочные фрагменты; каждому типу
 * назначается отдельный CSS-класс, поэтому части одной строки можно окрашивать
 * по-разному, хотя сам HTML input такой возможности не предоставляет.
 * Положение прокрутки настоящего поля передаётся зеркалу, чтобы оба слоя
 * оставались совмещёнными даже для длинного или многострочного содержимого.
 * После незакрытой открывающей скобки компонент анализирует текст до текущего
 * курсора и показывает только те варианты из tags, которые подходят по
 * введённому префиксу. При выборе подсказки введённый фрагмент заменяется
 * полным тегом, после чего курсор программно возвращается сразу за закрывающую
 * скобку. Список ошибочных тегов вычисляется отдельно и может передаваться
 * наружу через onTagValidationChange, поэтому визуальная проверка не смешана
 * с бизнес-логикой формы. Такой подход сохраняет привычное поведение
 * нативного поля и одновременно добавляет подсветку и автодополнение тегов.
 */

import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    get_invalid_tags,
    get_tag_suggestion_state,
    tokenize_tag_text,
} from './tag_utils';


const value_event = (event, value) => ({
    ...event,
    currentTarget: {
        ...event.currentTarget,
        value,
    },
    target: {
        ...event.target,
        value,
    },
});


const TagMirror = ({
    multiline,
    scroll_left,
    scroll_top,
    tags,
    value,
}) => {
    const parts = useMemo(
        () => tokenize_tag_text(value, tags),
        [tags, value],
    );

    const style = {
        transform: multiline
            ? (
                `translate(${-scroll_left}px, `
                + `${-scroll_top}px)`
            )
            : `translateX(${-scroll_left}px)`,
    };

    return (
        <div
            className={[
                'app-input-tags__mirror',
                multiline
                    ? 'app-input-tags__mirror--multiline'
                    : '',
            ].filter(Boolean).join(' ')}
            aria-hidden="true"
        >
            <div
                className="app-input-tags__mirror-content"
                style={style}
            >
                {parts.map((part, index) => (
                    <span
                        key={`${part.kind}_${index}`}
                        className={
                            `app-input-tags__${part.kind}`
                        }
                    >
                        {part.text}
                    </span>
                ))}
            </div>
        </div>
    );
};


const TagSuggestions = ({
    on_select,
    state,
}) => {
    if (!state) {
        return null;
    }

    return (
        <div
            className="app-input-tags__suggestions"
            role="listbox"
        >
            {state.options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    className="app-input-tags__suggestion"
                    onMouseDown={(event) => {
                        event.preventDefault();
                        on_select(option.value);
                    }}
                >
                    <strong>{`{${option.value}}`}</strong>
                    {option.label && (
                        <span>{option.label}</span>
                    )}
                </button>
            ))}
        </div>
    );
};


const TaggedInput = ({
    className = '',
    multiline = false,
    onBlur,
    onChange,
    onFocus,
    onTagValidationChange,
    tags,
    value = '',
    ...props
}) => {
    const control_ref = useRef(null);
    const [caret_position, set_caret_position] = useState(0);
    const [focused, set_focused] = useState(false);
    const [scroll_left, set_scroll_left] = useState(0);
    const [scroll_top, set_scroll_top] = useState(0);
    const invalid_tags = useMemo(
        () => get_invalid_tags(value, tags),
        [tags, value],
    );
    const suggestion_state = useMemo(
        () => (
            focused
                ? get_tag_suggestion_state(
                    value,
                    caret_position,
                    tags,
                )
                : null
        ),
        [
            caret_position,
            focused,
            tags,
            value,
        ],
    );

    useEffect(() => {
        onTagValidationChange?.(invalid_tags);
    }, [
        invalid_tags,
        onTagValidationChange,
    ]);

    const sync_caret = (event) => {
        set_caret_position(
            event.currentTarget.selectionStart
            ?? String(value ?? '').length,
        );
    };

    const handle_change = (event) => {
        sync_caret(event);
        onChange?.(event);
    };

    const handle_focus = (event) => {
        set_focused(true);
        sync_caret(event);
        onFocus?.(event);
    };

    const handle_blur = (event) => {
        set_focused(false);
        onBlur?.(event);
    };

    const handle_scroll = (event) => {
        set_scroll_left(event.currentTarget.scrollLeft);
        set_scroll_top(event.currentTarget.scrollTop);
    };

    const select_tag = (tag_name) => {
        const state = suggestion_state;
        const control = control_ref.current;

        if (!state || !control) {
            return;
        }

        const text = String(value ?? '');
        const replacement = `{${tag_name}}`;
        const next_value = (
            text.slice(0, state.start)
            + replacement
            + text.slice(state.end)
        );
        const next_caret = (
            state.start + replacement.length
        );
        const event = {
            currentTarget: control,
            target: control,
        };

        onChange?.(value_event(event, next_value));
        set_caret_position(next_caret);

        window.requestAnimationFrame(() => {
            control.focus();
            control.setSelectionRange(
                next_caret,
                next_caret,
            );
        });
    };

    const common_props = {
        ...props,
        ref: control_ref,
        value,
        onBlur: handle_blur,
        onChange: handle_change,
        onClick: sync_caret,
        onFocus: handle_focus,
        onKeyUp: sync_caret,
        onScroll: handle_scroll,
        className: [
            'app-input',
            'app-input-tags__control',
            className,
        ].filter(Boolean).join(' '),
    };

    return (
        <div
            className={[
                'app-input-tags',
                multiline
                    ? 'app-input-tags--multiline'
                    : '',
            ].filter(Boolean).join(' ')}
        >
            <TagMirror
                multiline={multiline}
                scroll_left={scroll_left}
                scroll_top={scroll_top}
                tags={tags}
                value={value}
            />

            {multiline ? (
                <textarea {...common_props} />
            ) : (
                <input
                    {...common_props}
                    type="text"
                />
            )}

            <TagSuggestions
                on_select={select_tag}
                state={suggestion_state}
            />
        </div>
    );
};


export default TaggedInput;
